import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Mail, 
  Settings,
  LogOut,
  Menu,
  X,
  Briefcase,
  ClipboardList,
  FileInput,
  Palette,
  BarChart3,
  UserCircle,
  Building2,
  PenTool,
  Radar,
  Map,
  Sparkles,
  Globe,
  Archive,
  MessageSquare,
  Target,
  ChevronDown,
  Layers,
  Shield,
  BookOpen,
  Eye,
  ArrowLeftRight,
  CheckCircle,
  Route,
  Bell,
  Inbox,
  Wand2
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useApi } from '../../hooks/useApi';
import { getInitials } from '../../lib/utils';
import { EngineHealthBadge } from './EngineHealthBadge';

const API = process.env.REACT_APP_BACKEND_URL || '';

// DH Design branding - using text-based logo for now
const BRAND_NAME = "DH Design";

export function Navbar() {
  const { 
    user, 
    logout, 
    isAdmin, 
    isCreativeLead, 
    isExecutiveViewer,
    viewingAsUser,
    isViewingAsOther,
    allUsers,
    switchUserContext,
    clearViewingContext
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userSwitcherOpen, setUserSwitcherOpen] = useState(false);

  // Phase 3 — Suggested Signals draft count for navbar bell badge
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [draftCount, setDraftCount] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await apiRef.current.get('/signal-drafts/count');
        if (!cancelled) setDraftCount(res?.count || 0);
      } catch (e) {
        // silent
      }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Phase 8 — Lead form submissions unread badge (Sales Intelligence dropdown)
  const [unreadSubmissionsCount, setUnreadSubmissionsCount] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await apiRef.current.get('/lead-submissions/unread-counts');
        const counts = res?.counts || {};
        const total = Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
        if (!cancelled) setUnreadSubmissionsCount(total);
      } catch (e) { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Build Inquiries "new" badge — admin-only. Polls the cheap count
  // endpoint on a 60s interval so the navbar surfaces unread leads the
  // moment they arrive via /build.html → /api/contact/build.
  const [buildInquiriesNewCount, setBuildInquiriesNewCount] = useState(0);
  useEffect(() => {
    if (!user || user.role !== 'admin') return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await apiRef.current.get('/admin/build-inquiries/count');
        if (!cancelled) setBuildInquiriesNewCount(Number(res?.new_count) || 0);
      } catch (e) { /* silent — non-critical */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchUser = (targetUser) => {
    switchUserContext(targetUser);
    setUserSwitcherOpen(false);
  };

  const handleReturnToOwnAccount = () => {
    clearViewingContext();
    setUserSwitcherOpen(false);
  };

  // Regular nav items (excluding dropdown items and Users which is now in profile menu)
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'sales_rep', 'creative_lead', 'executive_viewer'] },
    { path: '/territories', label: 'Territories', icon: Map, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/pipeline', label: 'Style Opportunities', icon: Briefcase, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/contacts', label: 'Contacts', icon: UserCircle, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/organizations', label: 'Organizations', icon: Building2, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/engagements', label: 'Engagements', icon: Briefcase, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/deck-library', label: 'Style Archive', icon: Archive, roles: ['admin', 'sales_rep', 'executive_viewer'] },
  ];

  // Intelligence dropdown items (Market Intelligence + Industry Architect + Route Intelligence)
  const intelligenceItems = [
    { path: '/market-intelligence', label: 'Market Intelligence', icon: Radar, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/industry-architect', label: 'Industry Architect', icon: BookOpen, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/route-intelligence', label: 'Route Intelligence', icon: Route, roles: ['admin', 'sales_rep'] },
    { path: '/decision-maker-discovery', label: 'Decision Makers', icon: Target, roles: ['admin', 'sales_rep'] },
  ];

  // Conversations dropdown items
  const conversationsItems = [
    { path: '/emails', label: 'Conversations', icon: MessageSquare, roles: ['admin', 'sales_rep'] },
    { path: '/email-analytics', label: 'Analytics', icon: BarChart3, roles: ['admin', 'sales_rep', 'executive_viewer'] },
  ];

  // Vision Decks dropdown items
  const visionDecksItems = [
    { path: '/deck-intake', label: 'Premium Deck Intake', icon: FileInput, roles: ['admin', 'sales_rep'] },
    { path: '/vision-decks', label: 'Vision Decks', icon: Layers, roles: ['admin', 'sales_rep', 'executive_viewer'] },
    { path: '/media-studio', label: 'Media Studio', icon: Wand2, roles: ['admin', 'sales_rep', 'creative_lead'] },
    { path: '/lead-submissions', label: 'Submissions', icon: Inbox, roles: ['admin', 'sales_rep'], showBadge: true },
  ];

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const filteredIntelligenceItems = intelligenceItems.filter(item =>
    item.roles.includes(user?.role)
  );

  const filteredConversationsItems = conversationsItems.filter(item =>
    item.roles.includes(user?.role)
  );

  const filteredVisionDecksItems = visionDecksItems.filter(item =>
    item.roles.includes(user?.role)
  );

  const isActive = (path) => location.pathname === path;
  const isIntelligenceActive = intelligenceItems.some(item => location.pathname === item.path);
  const isConversationsActive = conversationsItems.some(item => location.pathname === item.path);
  const isVisionDecksActive = visionDecksItems.some(item => location.pathname === item.path || location.pathname.startsWith('/vision-decks'));

  return (
    <>
      {/* Admin Viewing As Banner */}
      {isViewingAsOther && viewingAsUser && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center sticky top-0 z-[60]" data-testid="viewing-as-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm">
            <Eye className="w-4 h-4" />
            <span>
              Viewing as <strong>{viewingAsUser.name || viewingAsUser.email}</strong>
              <span className="opacity-75 ml-1">({viewingAsUser.role})</span>
            </span>
            <Button 
              size="sm" 
              variant="secondary" 
              className="h-6 px-2 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={handleReturnToOwnAccount}
              data-testid="return-to-account-btn"
            >
              <ArrowLeftRight className="w-3 h-3 mr-1" />
              Return to My Account
            </Button>
          </div>
        </div>
      )}
      
      <nav className="glass-nav sticky top-0 z-50" data-testid="main-navbar" style={{ top: isViewingAsOther ? '40px' : '0' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="nav-logo">
              <div className="flex items-center">
                <span className="text-xl font-serif tracking-wide text-[#1A1A1A]">
                  <span className="text-[#B89B5E] font-semibold">DH</span>
                  <span className="font-light ml-1">Design</span>
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {filteredNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors rounded-sm ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
            
            {/* Intelligence Dropdown (Market Intelligence + Industry Architect) */}
            {filteredIntelligenceItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-intelligence-dropdown"
                    className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors rounded-sm ${
                      isIntelligenceActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Radar className="w-4 h-4" />
                    Intelligence
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48" data-testid="intelligence-dropdown-content">
                  {filteredIntelligenceItems.map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link
                        to={item.path}
                        data-testid={`nav-dropdown-${item.path.slice(1)}`}
                        className={`flex items-center gap-2 cursor-pointer ${
                          isActive(item.path) ? 'bg-muted' : ''
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Conversations Dropdown */}
            {filteredConversationsItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-conversations-dropdown"
                    className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors rounded-sm ${
                      isConversationsActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Conversations
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44" data-testid="conversations-dropdown-content">
                  {filteredConversationsItems.map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link
                        to={item.path}
                        data-testid={`nav-dropdown-${item.path.slice(1)}`}
                        className={`flex items-center gap-2 cursor-pointer ${
                          isActive(item.path) ? 'bg-muted' : ''
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Vision Decks Dropdown */}
            {filteredVisionDecksItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-vision-decks-dropdown"
                    className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors rounded-sm relative ${
                      isVisionDecksActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    Vision Decks
                    <ChevronDown className="w-3 h-3 ml-1" />
                    {unreadSubmissionsCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#B89B5E] rounded-full ring-2 ring-background" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48" data-testid="vision-decks-dropdown-content">
                  {filteredVisionDecksItems.map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link
                        to={item.path}
                        data-testid={`nav-dropdown-${item.path.slice(1)}`}
                        className={`flex items-center gap-2 cursor-pointer ${
                          isActive(item.path) ? 'bg-muted' : ''
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="flex-1">{item.label}</span>
                        {item.showBadge && unreadSubmissionsCount > 0 && (
                          <span className="ml-auto bg-[#B89B5E] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-testid="nav-submissions-badge">
                            {unreadSubmissionsCount > 99 ? '99+' : unreadSubmissionsCount}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            {/* Document 2 · Phase 1 — Engine Health Badge */}
            {user && <EngineHealthBadge />}

            {/* Phase 3 — Drafted Signals Bell */}
            {user && (
              <Link to="/market-intelligence?tab=radar#suggested-signals-draft" data-testid="navbar-drafts-bell">
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative h-10 w-10 p-0 hover:bg-[#B89B5E]/10"
                  aria-label={`${draftCount} drafted signals awaiting review`}
                >
                  <Bell className="h-5 w-5 text-stone-600" />
                  {draftCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B89B5E] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
                      data-testid="drafts-bell-badge"
                    >
                      {draftCount > 99 ? '99+' : draftCount}
                    </span>
                  )}
                </Button>
              </Link>
            )}

            {/* Build Inquiries — pulsing chip directly next to the bell.
                Admin-only; reuses buildInquiriesNewCount which is already
                polled on a 60 s interval. No extra network cost.
                Hidden when zero so non-noise UX is preserved. */}
            {user?.role === 'admin' && buildInquiriesNewCount > 0 && (
              <Link
                to="/admin/build-inquiries"
                data-testid="navbar-build-inquiries-chip"
                aria-label={`${buildInquiriesNewCount} new build inquiry${buildInquiriesNewCount === 1 ? '' : 's'}`}
                className="relative inline-flex items-center gap-1.5 h-8 pl-2 pr-2.5 rounded-full bg-gradient-to-r from-[#B89B5E] to-[#D4AF50] text-white text-xs font-semibold shadow-sm hover:shadow-md hover:from-[#A98A4D] hover:to-[#C39E40] transition-all"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                </span>
                <span className="hidden sm:inline">
                  {buildInquiriesNewCount > 99 ? '99+' : buildInquiriesNewCount} new
                </span>
                <span className="sm:hidden">
                  {buildInquiriesNewCount > 99 ? '99+' : buildInquiriesNewCount}
                </span>
              </Link>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0" data-testid="user-menu-trigger">
                  <Avatar className="h-10 w-10 rounded-full border-2 border-transparent hover:border-[#B89B5E] transition-colors">
                    {user?.signature_headshot_url ? (
                      <AvatarImage 
                        src={user.signature_headshot_url.startsWith('http') 
                          ? user.signature_headshot_url 
                          : `${API}${user.signature_headshot_url}`
                        } 
                        alt={user.name}
                        className="object-cover"
                      />
                    ) : null}
                    <AvatarFallback className="rounded-full bg-muted text-xs font-medium">
                      {user ? getInitials(user.name) : '?'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" data-testid="user-menu-content">
                <div className="flex items-center justify-start gap-3 p-3">
                  <Avatar className="h-12 w-12 rounded-full">
                    {user?.signature_headshot_url ? (
                      <AvatarImage 
                        src={user.signature_headshot_url.startsWith('http') 
                          ? user.signature_headshot_url 
                          : `${API}${user.signature_headshot_url}`
                        } 
                        alt={user.name}
                        className="object-cover"
                      />
                    ) : null}
                    <AvatarFallback className="rounded-full bg-muted text-sm font-medium">
                      {user ? getInitials(user.name) : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <p className="text-[10px] text-[#B89B5E] uppercase tracking-wider font-medium">{user?.role?.replace('_', ' ')}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {/* Users - Admin only */}
                {user?.role === 'admin' && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/users" className="cursor-pointer" data-testid="user-menu-access-management">
                        <Shield className="mr-2 h-4 w-4" />
                        User Access Management
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/users" className="cursor-pointer" data-testid="user-menu-users">
                        <Users className="mr-2 h-4 w-4" />
                        Users
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/workspace-settings" className="cursor-pointer" data-testid="user-menu-workspace-settings">
                        <Settings className="mr-2 h-4 w-4" />
                        Workspace Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/ai-usage" className="cursor-pointer" data-testid="user-menu-ai-usage">
                        <Sparkles className="mr-2 h-4 w-4" />
                        AI Usage
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/admin/build-inquiries"
                        className="cursor-pointer flex items-center justify-between"
                        data-testid="user-menu-build-inquiries"
                      >
                        <span className="flex items-center">
                          <Inbox className="mr-2 h-4 w-4" />
                          Build Inquiries
                        </span>
                        {buildInquiriesNewCount > 0 && (
                          <Badge
                            variant="default"
                            className="ml-2 h-5 px-1.5 text-[10px] font-semibold bg-[#B89B5E] hover:bg-[#B89B5E] text-white rounded-full"
                            data-testid="build-inquiries-badge"
                          >
                            {buildInquiriesNewCount > 99 ? '99+' : buildInquiriesNewCount}
                          </Badge>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {/* User Context Switcher - Admin only */}
                    <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      View As User
                    </DropdownMenuLabel>
                    {isViewingAsOther && (
                      <DropdownMenuItem 
                        onSelect={(e) => {
                          e.preventDefault();
                          handleReturnToOwnAccount();
                        }}
                        className="cursor-pointer text-amber-600 bg-amber-50"
                      >
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        Return to My Account
                      </DropdownMenuItem>
                    )}
                    {allUsers.filter(u => u.id !== user?.id && u.approval_status !== 'pending').slice(0, 8).map((u) => (
                      <DropdownMenuItem 
                        key={u.id}
                        onSelect={(e) => {
                          e.preventDefault();
                          handleSwitchUser(u);
                        }}
                        className={`cursor-pointer ${viewingAsUser?.id === u.id ? 'bg-amber-100' : ''}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs bg-muted">
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{u.name}</div>
                            <div className="text-[10px] text-muted-foreground truncate">{u.role}</div>
                          </div>
                          {viewingAsUser?.id === u.id && (
                            <CheckCircle className="w-4 h-4 text-amber-600" />
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer" data-testid="user-menu-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/signature" className="cursor-pointer" data-testid="user-menu-signature">
                    <PenTool className="mr-2 h-4 w-4" />
                    Signature Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/email" className="cursor-pointer" data-testid="user-menu-email-settings">
                    <Mail className="mr-2 h-4 w-4" />
                    Email Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/publishing" className="cursor-pointer" data-testid="user-menu-publishing">
                    <Globe className="mr-2 h-4 w-4" />
                    Publishing Domain
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="user-menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border animate-fade-in" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {filteredNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.path.slice(1)}`}
                className={`flex items-center gap-3 px-3 py-2 text-sm rounded-sm ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
            
            {/* Intelligence Section */}
            {filteredIntelligenceItems.length > 0 && (
              <>
                <div className="pt-2 pb-1 px-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Radar className="w-3 h-3" />
                    Intelligence
                  </span>
                </div>
                {filteredIntelligenceItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.path.slice(1)}`}
                    className={`flex items-center gap-3 px-3 py-2 text-sm rounded-sm ml-2 ${
                      isActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
              </>
            )}
            
            {/* Conversations Section */}
            {filteredConversationsItems.length > 0 && (
              <>
                <div className="pt-2 pb-1 px-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <MessageSquare className="w-3 h-3" />
                    Conversations
                  </span>
                </div>
                {filteredConversationsItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.path.slice(1)}`}
                    className={`flex items-center gap-3 px-3 py-2 text-sm rounded-sm ml-2 ${
                      isActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
              </>
            )}
            
            {/* Vision Decks Section */}
            {filteredVisionDecksItems.length > 0 && (
              <>
                <div className="pt-2 pb-1 px-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-3 h-3" />
                    Vision Decks
                  </span>
                </div>
                {filteredVisionDecksItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.path.slice(1)}`}
                    className={`flex items-center gap-3 px-3 py-2 text-sm rounded-sm ml-2 ${
                      isActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
              </>
            )}
            
            {/* Users - Admin only (in mobile menu) */}
            {user?.role === 'admin' && (
              <>
                <div className="pt-2 pb-1 px-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Settings className="w-3 h-3" />
                    Admin
                  </span>
                </div>
                <Link
                  to="/users"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-users"
                  className={`flex items-center gap-3 px-3 py-2 text-sm rounded-sm ml-2 ${
                    isActive('/users')
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Users
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
}
